import React, { useState, FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getChecklist } from "../../../../api/checkilstApi";
import { getTipo } from "../../../../api/tipoApi";
import { getModelos } from "../../../../api/modeloApi";
import { getEmpresas } from "../../../../api/empresaApi";
import { createAcao } from "../../../../api/acaoApi";
import Formulario, { FormField } from "../../../../components/Formulario";
import { getTipoLocal } from "../../../../api/tipoLocalApi";
import { buscaChecklist } from "../../../../types/checklistData";
import { buscaTipo } from "../../../../types/tipo";
import { buscaModelo } from "../../../../types/modelo";
import { Empresa } from "../../../../types/empresas";
import { buscaTipoLocal } from "../../../../types/tipoLocalData";

const AdicionarAcao: React.FC = () => {
  const [checklist, setChecklist] = useState<buscaChecklist | null>(null);
  const [tipo, setTipo] = useState<buscaTipo | null>(null);
  const [modelo, setModelo] = useState<buscaModelo | null>(null);
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [tipoLocal, setTipoLocal] = useState<buscaTipoLocal | null>(null);
  const [checklists, setChecklists] = useState<buscaChecklist[]>([]);
  const [tipos, setTipos] = useState<buscaTipo[]>([]);
  const [modelos, setModelos] = useState<buscaModelo[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [tiposLocais, setTiposLocais] = useState<buscaTipoLocal[]>([]);
  const [clienteObrigatorio, setClienteObrigatorio] = useState<boolean>(false);
  const [periodico, setPeriodico] = useState<boolean>(false);
  const [periodo, setPeriodo] = useState<number>(0);
  const [entrega, setEntrega] = useState<boolean>(false);
  const [ordem, setOrdem] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const [checklistsData, tiposData, modelosData, empresasData, tipoLocalData] =
          await Promise.all([
            getChecklist(token),
            getTipo(token),
            getModelos(token),
            getEmpresas(token),
            getTipoLocal(token),
          ]);
        setChecklists(checklistsData);
        setTipos(tiposData);
        setModelos(modelosData);
        setEmpresas(empresasData);
        setTiposLocais(tipoLocalData);
      } catch (error) {
        toast.error("Erro ao buscar dados!");
        console.error("Erro ao buscar dados:", error);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado");
      }

      if (!checklist || !tipo || !modelo || !empresa || !tipoLocal) {
        toast.error("Todos os campos obrigatórios devem ser preenchidos!");
        return;
      }
      const acaoData = {
        id_checklist: checklist.id,
        id_tipo: tipo.id,
        id_modelo: modelo.id,
        id_empresa: empresa.id,
        cliente_obrigatorio_acoes: clienteObrigatorio,
        periodico_acao: periodico,
        periodo_acao: periodo,
        entrega_acao: entrega,
        id_tipo_local: tipoLocal.id,
        ordem_acao: ordem,
      };      

      await createAcao(token, acaoData);

      toast.success("Ação criada com sucesso!");
      navigate("/acoes");
    } catch (error) {
      toast.error("Erro ao adicionar ação!");
      console.error("Erro ao adicionar ação:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleChecklistChange = (event: React.SyntheticEvent, value: buscaChecklist | null) => {
    setChecklist(value);
  };  

  const handleTipoChange = (event: React.SyntheticEvent, value: buscaTipo) => {
    setTipo(value);
  };

  const handleModeloChange = (event: React.SyntheticEvent, value: buscaModelo) => {
    setModelo(value);
  };

  const handleEmpresaChange = (event: React.SyntheticEvent, value: Empresa) => {
    setEmpresa(value);
  };

  const handleTipoLocalChange = (event: React.SyntheticEvent, value: buscaTipoLocal) => {
    setTipoLocal(value);
  };

  const fields: FormField[] = [
    {
      label: "Checklist",
      type: "autocomplete",
      value: checklist,
      required: true,
      onChangeAutocomplete: handleChecklistChange,
      options: checklists,
      getOptionLabel: (option: buscaChecklist) => option.descricao_checklist,
    },
    {
      label: "Tipo",
      type: "autocomplete",
      value: tipo,
      required: true,
      onChangeAutocomplete: handleTipoChange,
      options: tipos,
      getOptionLabel: (option: buscaTipo) => option.nome_tipo,
    },
    {
      label: "Modelo",
      type: "autocomplete",
      value: modelo,
      required: true,
      onChangeAutocomplete: handleModeloChange,
      options: modelos,
      getOptionLabel: (option: buscaModelo) => option.descricao_modelo,
    },
    {
      label: "Empresa",
      type: "autocomplete",
      value: empresa,
      required: true,
      onChangeAutocomplete: handleEmpresaChange,
      options: empresas,
      getOptionLabel: (option: Empresa) => option.nome_empresa,
    },
    {
      label: "Cliente Obrigatório",
      type: "checkbox",
      value: clienteObrigatorio,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setClienteObrigatorio(e.target.checked),
    },
    {
      label: "Ação Periódica",
      type: "checkbox",
      value: periodico,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setPeriodico(e.target.checked),
    },
    {
      label: "Período da Ação",
      type: "number",
      value: periodo,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setPeriodo(Number(e.target.value)),
    },
    {
      label: "Entrega da Ação",
      type: "checkbox",
      value: entrega,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setEntrega(e.target.checked),
    },
    {
      label: "Tipo Local",
      type: "autocomplete",
      value: tipoLocal,
      required: true,
      onChangeAutocomplete: handleTipoLocalChange,
      options: tiposLocais,
      getOptionLabel: (option: buscaTipoLocal) => option.descriacao_tipo_local,
    },
    {
      label: "Ordem da Ação",
      type: "number",
      value: ordem,
      required: true,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setOrdem(Number(e.target.value)),
    },
  ];

  return (
    <Formulario
      title="Adicionar Ação"
      fields={fields}
      onSubmit={handleSubmit}
      onBack={handleBack}
      loading={loading}
    />
  );
};

export default AdicionarAcao;
